import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiX,
  FiZap,
  FiFileText,
  FiTrendingDown,
  FiDollarSign,
  FiUserPlus,
  FiHome,
  FiBarChart2,
  FiMail,
} from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { isViewerHome } from '../utils/access';
import { isOperationalWriteDisabled } from '../utils/writeAccess';
import { needsSubscriptionPayment } from '../utils/subscriptionGate';

const PUBLIC_PATHS = new Set([
  '/',
  '/planes',
  '/funcionalidades',
  '/quienes-somos',
  '/ayuda',
  '/contacto',
  '/terminos',
  '/privacidad',
  '/login',
  '/register',
  '/forgot-password',
  '/restablecer-clave',
  '/pago/resultado',
  '/activar-cuenta',
]);

function isAppRoute(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return false;
  if (pathname.startsWith('/contador/compartido')) return false;
  if (pathname.startsWith('/admin')) return false;
  if (pathname === '/completar-perfil' || pathname === '/aceptar-contador') return false;
  return true;
}

/** Ancho fijo para todas las acciones (cubre “Regularizar plan” / “Nuevo cliente”). */
const ACTION_CHIP_WIDTH = 'w-48';

function ActionChip({ icon: Icon, label, onClick, modoOscuro, delayMs = 0 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-center gap-2.5 pl-3 pr-3 py-2.5 rounded-full border shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${ACTION_CHIP_WIDTH} min-h-[3.25rem] ${
        modoOscuro
          ? 'bg-gray-900 border-gray-600 text-white hover:bg-gray-800'
          : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
      }`}
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      <span
        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
          modoOscuro ? 'bg-[#004AAD]/35 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'
        }`}
      >
        <Icon className="w-4 h-4" aria-hidden />
      </span>
      <span className="text-sm font-semibold text-left flex-1 min-w-0 truncate">{label}</span>
    </button>
  );
}

export default function QuickActionsFab() {
  const { user, loading } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const visible = Boolean(user && !loading && isAppRoute(location.pathname));

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const readOnly = isOperationalWriteDisabled(user);
  const paymentPending = needsSubscriptionPayment(user);
  const viewerHome = isViewerHome(user);
  const contadorBottomBar = location.pathname === '/contador';

  const actions = useMemo(() => {
    const go = (path) => () => {
      setOpen(false);
      navigate(path);
    };

    const contactAction = {
      id: 'contacto',
      icon: FiMail,
      label: 'Contacto',
      onClick: () => {
        setOpen(false);
        window.dispatchEvent(new CustomEvent('openContactModal'));
      },
    };

    if (paymentPending) {
      return [
        {
          id: 'planes',
          icon: FiDollarSign,
          label: 'Regularizar plan',
          onClick: go('/planes?pago=pendiente'),
        },
        contactAction,
      ];
    }

    const list = [];

    if (!viewerHome) {
      list.push({
        id: 'inicio',
        icon: FiHome,
        label: 'Inicio',
        onClick: go('/inicio'),
      });
    }

    if (!readOnly) {
      list.push(
        {
          id: 'venta-completa',
          icon: FiFileText,
          label: 'Venta completa',
          onClick: go('/facturas?tab=crear&modo=completo'),
        },
        {
          id: 'venta',
          icon: FiZap,
          label: 'Venta rápida',
          onClick: go('/facturas?tab=crear&modo=rapido'),
        },
        {
          id: 'gasto',
          icon: FiTrendingDown,
          label: 'Nuevo gasto',
          onClick: go('/egresos?tab=cargar'),
        },
        {
          id: 'cliente',
          icon: FiUserPlus,
          label: 'Nuevo cliente',
          onClick: go('/clientes?tab=crear'),
        },
      );
    }

    list.push(
      {
        id: 'cobrar',
        icon: FiDollarSign,
        label: 'Cobrar',
        onClick: go('/cobranzas'),
      },
      {
        id: 'reportes',
        icon: FiBarChart2,
        label: 'Reportes',
        onClick: go('/reportes'),
      },
    );

    list.push(contactAction);

    return list;
  }, [navigate, paymentPending, readOnly, viewerHome]);

  if (!visible || actions.length === 0) return null;

  const bottomClass = contadorBottomBar
    ? 'bottom-[5.5rem] lg:bottom-5'
    : 'bottom-5';

  return createPortal(
    <>
      {open && (
        <button
          type="button"
          className="fixed inset-0 z-[42] cursor-default bg-black/20 backdrop-blur-[1px]"
          aria-label="Cerrar acciones rápidas"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        className={`fixed z-[43] right-4 flex flex-col-reverse items-end gap-2.5 ${bottomClass} ${ACTION_CHIP_WIDTH} pr-[max(0px,env(safe-area-inset-right))] pb-[max(0px,env(safe-area-inset-bottom))]`}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Cerrar acciones rápidas' : 'Abrir acciones rápidas'}
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl border-2 transition-all duration-200 hover:scale-105 active:scale-95 ${
            open
              ? modoOscuro
                ? 'bg-gray-800 border-gray-600 text-white rotate-0'
                : 'bg-gray-900 border-gray-900 text-white'
              : 'bg-[#1F80FF] border-[#1F80FF] text-white hover:bg-[#004AAD] hover:border-[#004AAD]'
          }`}
        >
          {open ? <FiX className="w-6 h-6" /> : <FiPlus className="w-6 h-6" />}
        </button>

        {open &&
          actions.map((action, index) => (
            <ActionChip
              key={action.id}
              icon={action.icon}
              label={action.label}
              onClick={action.onClick}
              modoOscuro={modoOscuro}
              delayMs={index * 40}
            />
          ))}
      </div>

    </>,
    document.body,
  );
}
